/**
 * Clerk URL Utilities
 *
 * Handles compression and decompression of Google URLs for Clerk metadata storage.
 * Supports backwards compatibility with full URLs.
 */

// ============================================================================
// Web App URL (Google Apps Script) Utilities
// ============================================================================

const WEBAPP_URL_PREFIX = "https://script.google.com/macros/s/";
const WEBAPP_URL_SUFFIX = "/exec";
const WEBAPP_URL_PATTERN =
  /https:\/\/script\.google\.com\/macros\/s\/([^\/]+)\/exec/;

/**
 * Check if a string is a full Web App URL or just a script ID
 */
export function isFullWebAppUrl(url: string | undefined): boolean {
  if (!url) return false;
  return url.startsWith(WEBAPP_URL_PREFIX);
}

/**
 * Extract script ID from Web App URL
 * Handles both full URLs and bare IDs (backwards compatible)
 */
export function extractWebAppId(
  url: string | undefined,
): string | undefined {
  if (!url || url.trim() === "") return undefined;

  // If it's already just an ID (doesn't start with https://), return as-is
  if (!url.startsWith("http")) {
    return url;
  }

  // Extract from full URL
  const match = url.match(WEBAPP_URL_PATTERN);
  return match ? match[1] : undefined;
}

/**
 * Build full Web App URL from script ID
 * Handles both full URLs and bare IDs (backwards compatible)
 */
export function buildWebAppUrl(
  idOrUrl: string | undefined,
): string | undefined {
  if (!idOrUrl || idOrUrl.trim() === "") return undefined;

  // If it's already a full URL, return as-is
  if (idOrUrl.startsWith("http")) {
    return idOrUrl;
  }

  // Build from ID
  return `${WEBAPP_URL_PREFIX}${idOrUrl}${WEBAPP_URL_SUFFIX}`;
}

// ============================================================================
// Sheet URL (Google Sheets) Utilities
// ============================================================================

const SHEET_URL_PREFIX = "https://docs.google.com/spreadsheets/d/";
const SHEET_URL_PATTERN =
  /https:\/\/docs\.google\.com\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/;

/**
 * Check if a string is a full Sheet URL or just a sheet ID
 */
export function isFullSheetUrl(url: string | undefined): boolean {
  if (!url) return false;
  return url.startsWith(SHEET_URL_PREFIX);
}

/**
 * Extract sheet ID from Sheet URL
 * Handles both full URLs and bare IDs (backwards compatible)
 */
export function extractSheetId(url: string | undefined): string | undefined {
  if (!url || url.trim() === "") return undefined;

  // If it's already just an ID (doesn't start with https://), return as-is
  if (!url.startsWith("http")) {
    return url;
  }

  // Extract from full URL
  const match = url.match(SHEET_URL_PATTERN);
  return match ? match[1] : undefined;
}

/**
 * Build full Sheet URL from sheet ID
 * Handles both full URLs and bare IDs (backwards compatible)
 * Note: We don't include gid parameter as it's not essential
 */
export function buildSheetUrl(idOrUrl: string | undefined): string | undefined {
  if (!idOrUrl || idOrUrl.trim() === "") return undefined;

  // If it's already a full URL, return as-is
  if (idOrUrl.startsWith("http")) {
    return idOrUrl;
  }

  // Build from ID (without gid parameter)
  return `${SHEET_URL_PREFIX}${idOrUrl}/edit`;
}

// ============================================================================
// Migration Utilities
// ============================================================================

export interface MigrationStats {
  totalAccounts: number;
  webAppUrlsCompressed: number;
  sheetUrlsCompressed: number;
  charactersSaved: number;
  alreadyCompressed: number;
  beforeSize: number;
  afterSize: number;
}

/**
 * Compress a single account's URLs (web app and sheet)
 * Returns new account object with compressed URLs and whether any changes were made
 */
export function compressAccountUrls(account: any): {
  account: any;
  changed: boolean;
  webAppCompressed: boolean;
  sheetCompressed: boolean;
  charsSaved: number;
} {
  let changed = false;
  let webAppCompressed = false;
  let sheetCompressed = false;
  let charsSaved = 0;

  const updatedAccount = { ...account };

  // Compress webAppUrl if it's a full URL
  if (account.webAppUrl && isFullWebAppUrl(account.webAppUrl)) {
    const scriptId = extractWebAppId(account.webAppUrl);
    if (scriptId) {
      charsSaved += account.webAppUrl.length - scriptId.length;
      updatedAccount.webAppUrl = scriptId;
      changed = true;
      webAppCompressed = true;
    }
  }

  // Compress sheetUrl if it's a full URL
  if (account.sheetUrl && isFullSheetUrl(account.sheetUrl)) {
    const sheetId = extractSheetId(account.sheetUrl);
    if (sheetId) {
      charsSaved += account.sheetUrl.length - sheetId.length;
      updatedAccount.sheetUrl = sheetId;
      changed = true;
      sheetCompressed = true;
    }
  }

  return {
    account: updatedAccount,
    changed,
    webAppCompressed,
    sheetCompressed,
    charsSaved,
  };
}

/**
 * Compress all URLs in user's Clerk metadata
 * This function should be called on user login to migrate their data
 */
export function compressClerkMetadataUrls(metadata: any): {
  metadata: any;
  stats: MigrationStats;
  needsUpdate: boolean;
} {
  const beforeSize = JSON.stringify(metadata).length;

  const stats: MigrationStats = {
    totalAccounts: 0,
    webAppUrlsCompressed: 0,
    sheetUrlsCompressed: 0,
    charactersSaved: 0,
    alreadyCompressed: 0,
    beforeSize,
    afterSize: 0,
  };

  let needsUpdate = false;
  const updatedMetadata = { ...metadata };

  // Compress URLs in accounts array
  if (Array.isArray(metadata.accounts)) {
    stats.totalAccounts = metadata.accounts.length;

    updatedMetadata.accounts = metadata.accounts.map((account: any) => {
      const {
        account: compressed,
        changed,
        webAppCompressed,
        sheetCompressed,
        charsSaved,
      } = compressAccountUrls(account);

      if (changed) {
        needsUpdate = true;
        stats.charactersSaved += charsSaved;

        if (webAppCompressed) {
          stats.webAppUrlsCompressed++;
        }

        if (sheetCompressed) {
          stats.sheetUrlsCompressed++;
        }
      } else {
        stats.alreadyCompressed++;
      }

      return compressed;
    });
  }

  // Also handle legacy accountSheetUrls object if it exists
  if (
    metadata.accountSheetUrls &&
    typeof metadata.accountSheetUrls === "object"
  ) {
    const accountSheetUrls = { ...metadata.accountSheetUrls };
    let sheetUrlsChanged = false;

    for (const [accountId, sheetUrl] of Object.entries(accountSheetUrls)) {
      if (typeof sheetUrl === "string" && isFullSheetUrl(sheetUrl)) {
        const sheetId = extractSheetId(sheetUrl);
        if (sheetId) {
          const originalLength = sheetUrl.length;
          accountSheetUrls[accountId] = sheetId;
          stats.charactersSaved += originalLength - sheetId.length;
          sheetUrlsChanged = true;
          needsUpdate = true;
        }
      }
    }

    if (sheetUrlsChanged) {
      updatedMetadata.accountSheetUrls = accountSheetUrls;
    }
  }

  stats.afterSize = JSON.stringify(updatedMetadata).length;

  return {
    metadata: updatedMetadata,
    stats,
    needsUpdate,
  };
}

// ============================================================================
// Helper Functions for Account Operations
// ============================================================================

/**
 * Get the full Web App URL for an account, handling both compressed and full URLs
 */
export function getAccountWebAppUrl(account: any): string | undefined {
  return buildWebAppUrl(account?.webAppUrl);
}

/**
 * Get the full Sheet URL for an account, handling both compressed and full URLs
 */
export function getAccountSheetUrl(account: any): string | undefined {
  return buildSheetUrl(account?.sheetUrl);
}

/**
 * Prepare account for Clerk storage (compress URLs before saving)
 */
export function prepareAccountForClerk(account: any): any {
  const { account: compressed } = compressAccountUrls(account);
  return compressed;
}

/**
 * Expand account from Clerk storage (build full URLs for app use)
 */
export function expandAccountFromClerk(account: any): any {
  return {
    ...account,
    webAppUrl: buildWebAppUrl(account.webAppUrl),
    sheetUrl: buildSheetUrl(account.sheetUrl),
  };
}
